import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

type ToastKind = "success" | "error" | "info";
/** One button on a toast — "Don't text" after moving a visit, say. */
export interface ToastAction { label: string; onClick: () => void }
interface Toast { id: string; kind: ToastKind; title: string; description?: string; action?: ToastAction }

interface ToastContextValue {
  show: (kind: ToastKind, title: string, description?: string, action?: ToastAction) => void;
  success: (title: string, description?: string, action?: ToastAction) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const show = useCallback((kind: ToastKind, title: string, description?: string, action?: ToastAction) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, kind, title, description, action }]);
    // A toast with something to click stays long enough to click it.
    setTimeout(() => remove(id), action ? 12000 : 5000);
  }, [remove]);

  // The context value MUST be referentially stable across re-renders.
  // Without useMemo here, every consumer's `useToast()` call returned a
  // fresh object each ToastProvider render — and any consumer effect
  // with `toast` in its deps refired on every render, causing inbox
  // chats to re-fetch + flash messages every few seconds.
  const success = useCallback((t: string, d?: string, a?: ToastAction) => show("success", t, d, a), [show]);
  const error   = useCallback((t: string, d?: string) => show("error", t, d), [show]);
  const info    = useCallback((t: string, d?: string) => show("info", t, d), [show]);
  const value = useMemo<ToastContextValue>(
    () => ({ show, success, error, info }),
    [show, success, error, info]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2 sm:bottom-6 sm:right-6">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              "pointer-events-auto flex items-start gap-3 rounded-xl border bg-zinc-950 p-3.5 shadow-xl shadow-black/30 animate-slide-in-right",
              t.kind === "success" && "border-emerald-500/30",
              t.kind === "error"   && "border-rose-500/30",
              t.kind === "info"    && "border-zinc-800",
            )}
          >
            {t.kind === "success" && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />}
            {t.kind === "error"   && <AlertCircle  className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />}
            {t.kind === "info"    && <Info         className="mt-0.5 h-4 w-4 shrink-0 text-zinc-300" />}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-zinc-100">{t.title}</p>
              {t.description && <p className="mt-0.5 text-xs text-zinc-400">{t.description}</p>}
              {t.action && (
                <button
                  type="button"
                  onClick={() => { t.action!.onClick(); remove(t.id); }}
                  className="mt-1.5 rounded-md border border-zinc-700 px-2 py-1 text-xs font-medium text-zinc-200 hover:bg-zinc-800"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => remove(t.id)}
              className="-mr-1 -mt-1 inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
